import { Component, Inject, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { I18nService } from '@containers/i18n/i18n.service'
import { UpdateTranslationManualy } from '@containers/i18n/store/i18n.actions'
import { TranslationObject } from '@containers/i18n/store/i18n.interface'
import { getDefaultTranslationsState } from '@containers/i18n/store/i18n.selector'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { ShowSnackbar } from '@shared/shared.actions'
import { Observable, Subscription, race } from 'rxjs'
import { filter, map, take, tap } from 'rxjs/operators'
import * as i18nTypes from '../../store/i18n.types'

@Component({
  selector: 'app-translation-editable-modal',
  templateUrl: './translation-editable-modal.component.html',
  styleUrls: ['./translation-editable-modal.component.scss'],
})
export class TranslationEditableModalComponent implements OnInit {
  keysControls: FormArray
  iso2code: string
  isLoading = false
  defaultTranslations
  private _subs: Array<Subscription> = []

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      iso2code: any
      translationKeys: Array<string>
      translationDefaults: Array<string>
    },
    private fb: FormBuilder,
    private matDialogRef: MatDialogRef<TranslationEditableModalComponent>,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private store: Store<any>,
    private updates$: Actions,
  ) {
    this.getDefaultTranslations()

    this.iso2code = this.translateService.store.currentLang

    this.keysControls = this.fb.array(
      this.data.translationKeys.map((key) =>
        this.fb.group({
          key: [{ value: key, disabled: true }, Validators.required],
          value: [
            this.translateService.instant(key, {
              defaultValue: this.data.translationDefaults
                ? this.data.translationDefaults[key]
                : undefined,
            }),
            Validators.required,
          ],
        }),
      ),
    )
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this._subs.filter((sub) => Boolean(sub)).forEach((sub) => sub.unsubscribe())
  }

  resetToDefault(formGroup: FormGroup) {
    this.crudHandler(
      this.i18nService
        .deleteKey(formGroup.get('key').value, this.iso2code)
        .pipe(
          tap((res) => {
            formGroup
              .get('value')
              .setValue(
                this.defaultTranslations[formGroup.get('key').value] ||
                  this.data.translationDefaults[formGroup.get('key').value] ||
                  formGroup.get('key').value,
              )
            formGroup.markAsPristine()
            formGroup.markAsUntouched()
            formGroup.updateValueAndValidity()
          }),
        ),
    )
  }

  updateTranslation(formGroup: FormGroup) {
    const translationObject: TranslationObject = {
      key: formGroup.get('key').value,
      value: formGroup.get('value').value,
    }

    this.crudHandler(
      this.i18nService.updateKey(translationObject, this.iso2code).pipe(
        tap((res) => {
          formGroup.markAsPristine()
          formGroup.markAsUntouched()
          formGroup.updateValueAndValidity()
        }),
      ),
    )
  }

  crudHandler(obs: Observable<any>, forceRestore = false) {
    this.isLoading = true
    obs.subscribe(
      (res) => {
        this.store.dispatch(new UpdateTranslationManualy(this.iso2code))

        const success = this.updates$.pipe(
          ofType(i18nTypes.UPDATE_TRANSLATIONS_MANUALY_SUCCESSFULLY),
          take(1),
        )

        const failure = this.updates$.pipe(
          ofType(i18nTypes.UPDATE_TRANSLATIONS_MANUALY_FAILURE),
          take(1),
        )

        race(success, failure).subscribe(() => {
          this.isLoading = false

          this.store.dispatch(
            new ShowSnackbar({
              type: 'success',
              message: this.translateService.instant(
                'OPERATION_FINISHED_SUCCESSFULLY',
              ),
            }),
          )
        })
      },
      (err) => {
        this.isLoading = false
      },
    )
  }

  cancel() {
    this.matDialogRef.close()
  }

  getDefaultTranslations() {
    const sub = this.store
      .pipe(
        select(getDefaultTranslationsState),
        filter((state) => Boolean(state)),
        map((state) => state.toJS()),
      )
      .subscribe((translations) => {
        6
        this.defaultTranslations = translations
      })

    this._subs.push(sub)
  }

  getCanRestoreToDefault(formGroup: FormGroup): boolean {
    return formGroup.get('key').value in
      this.translateService.store.translations[this.iso2code]
      ? true
      : false
  }
}
